import { Line } from "react-chartjs-2";
import { 
  Chart as ChartJS, 
  LineElement, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  Tooltip, 
  Legend 
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Card, Typography, Space } from "antd";

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend, ChartDataLabels);

export default function LineChart({ title, labels, datasets }) {
  const abbreviatedLabels = labels.map(label => {
    if (typeof label !== 'string') return label;
    if (label.includes('-SEM')) return label.split('-')[1];
    const parts = label.split('-');
    if (parts.length === 3) return `${parts[1]}-${parts[2]}`;
    if (parts.length === 2) return parts[1];
    return label;
  });

  const formatAbbreviated = (value) => {
    if (value === 0) return "0";
    if (!value) return "";
    if (value >= 1000000) return (value / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
    if (value >= 1000) return (value / 1000).toFixed(1).replace(/\.0$/, "") + "k";
    return Math.round(value);
  };

  const formatCurrency = (value) => 
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 0,
    }).format(value);

  const chartData = {
    labels: abbreviatedLabels,
    datasets: datasets.map(ds => ({
      ...ds,
      tension: 0.3, // Smoother lines
      pointRadius: 4,
      pointHoverRadius: 6,
      spanGaps: true,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { boxWidth: 10, font: { size: 10 } }
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${formatCurrency(context.parsed.y)} (${formatAbbreviated(context.parsed.y)})`;
          }
        }
      },
      datalabels: {
        align: 'top',
        anchor: 'end',
        offset: 2,
        formatter: formatAbbreviated,
        font: { size: 9, weight: 'bold' },
        color: (context) => context.dataset.borderColor,
        display: (context) => {
          // Only show labels for significant values or every few points to avoid clutter
          return context.dataset.data[context.dataIndex] > 0;
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: formatAbbreviated,
          font: { size: 10 }
        }
      },
      x: {
        ticks: {
          font: { size: 10 },
          maxRotation: 45,
          minRotation: 0
        }
      }
    },
    layout: {
      padding: { top: 20 }
    }
  };

  return (
    <Card 
      title={
        <Space direction="vertical" size={0}>
          <Typography.Text>{title}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: '11px', fontWeight: 'normal' }}>
            Sugerencia: Haz clic en los nombres en la leyenda para mostrar/ocultar usuarios
          </Typography.Text>
        </Space>
      } 
      size="small" 
      style={{ borderRadius: 10, height: "100%" }}
    >
      {datasets.length ? (
        <div style={{ height: "450px" }}>
          <Line data={chartData} options={options} />
        </div>
      ) : (
        <div style={{ height: "450px", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Typography.Text type="secondary">Cargando datos...</Typography.Text>
        </div>
      )}
    </Card>
  );
}
